import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { LoadingState } from '@/components/ui/LoadingState'
import type { VenueCampaignHistorySummaryView } from '@/lib/venue/campaignTypes'
import { readRateText } from '@/lib/venue/campaignViewModel'

const PROMOTION_COMING_SOON = '집계 준비 중'

function StatItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-slateish-600">{label}</p>
      <p className="text-base font-semibold text-ink-700">{value}</p>
    </div>
  )
}

/**
 * 관리 홈 — 운영 공지·지역 홍보 카드 2장(요구사항 "운영 공지/지역 홍보를 탭(또는 카드)로 구분").
 *
 * 🚨 지역 홍보 카드의 "비용·전환율"은 여전히 API에 기간 집계 원천이 없다(요약 EP에
 * `charge`/`conversionRatePercent` 필드가 없고, 캠페인 단건 결과 EP는 기간 집계가 아니다) —
 * `campaignCount`만 표시하고 나머지 두 지표는 "집계 준비 중" 고정 문구로 채운다(#729 §자율 결정 1을
 * #731 이후에도 유지, #732 설계 §자율 결정 2). 목록을 순회해 클라이언트가 합산하지 않는다.
 * "새 홍보 작성"은 `#731`이 대상 산출을 정상화하면서 활성 링크로 전환됐다(#729 당시의 비활성 버튼은
 * 폐기).
 */
export function NoticePromotionSummaryCards({
  venueId,
  notice,
  noticeLoading,
  promotion,
  promotionLoading,
}: {
  venueId: string
  notice: VenueCampaignHistorySummaryView | undefined
  noticeLoading: boolean
  promotion: VenueCampaignHistorySummaryView | undefined
  promotionLoading: boolean
}) {
  const navigate = useNavigate()

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <Card>
        <CardHeader
          title="운영 공지"
          description="팔로워·특정 예약자에게 무료로 보내는 운영 정보입니다. 광고 수신 동의가 필요하지 않습니다."
        />
        <CardBody className="space-y-4">
          {noticeLoading ? (
            <LoadingState rows={2} label="운영 공지 요약을 불러오는 중" />
          ) : notice ? (
            <div className="grid grid-cols-3 gap-3">
              <StatItem label="발송 건수" value={notice.campaignCount.toLocaleString('ko-KR')} />
              <StatItem label="누적 발송 대상" value={notice.recipientCount.toLocaleString('ko-KR')} />
              <StatItem label="평균 읽음률" value={readRateText(notice.readRatePercent)} />
            </div>
          ) : null}
          <Button
            type="button"
            onClick={() => {
              navigate(`/venues/${venueId}/notices/new`)
            }}
          >
            새 공지 작성
          </Button>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="지역 홍보"
          description="광고 수신에 동의한 지역 사용자에게 보내는 유료 홍보입니다. 실제 발송 건수를 기준으로 비용이 부과됩니다."
        />
        <CardBody className="space-y-4">
          {promotionLoading ? (
            <LoadingState rows={2} label="지역 홍보 요약을 불러오는 중" />
          ) : promotion ? (
            <div className="grid grid-cols-3 gap-3">
              <StatItem label="발송 건수" value={promotion.campaignCount.toLocaleString('ko-KR')} />
              <StatItem label="비용" value={PROMOTION_COMING_SOON} />
              <StatItem label="전환율" value={PROMOTION_COMING_SOON} />
            </div>
          ) : null}
          <Button
            type="button"
            onClick={() => {
              navigate(`/venues/${venueId}/notices/promotions/new`)
            }}
          >
            새 홍보 작성
          </Button>
        </CardBody>
      </Card>
    </div>
  )
}
